import AiMarkDefaultIcon from "../../../public/assets/icons/st/ai_mark.svg";
import AiMarkGradientIcon from "../../../public/assets/icons/st/ai_mark_gradient.svg";

import { Icon, IconProps } from "../Icon";
import {
  AiMarkCollapsePadding,
  AiMarkWithTooltipOrPopoverConfig,
} from "../../types/ai-marks";
import { AiMarkInteractive } from "./internal/AiMarkInteractive";
import { AiMarkIconAnimated } from "./internal/AiMarkIconAnimated";

import cx from "classnames";
import styles from "./AiMark.module.scss";

const ICON_PIXEL_SIZES: Record<NonNullable<IconProps["size"]>, number> = {
  small: 12,
  medium: 16,
  large: 24,
  xlarge: 32,
};

/**
 * Props for the AiMark component
 * @property {"default" | "gradient"} [type] - The visual style of the AI mark
 * @property {AiMarkWithTooltipOrPopoverConfig} [popoverOrTooltipConfig] - Configuration for popover or tooltip display with content and props
 * @property {boolean} [forceAnimate] - Forces the pulsing animation to play, intended for use within interactive parent components (e.g. on hover or focus of the parent)
 * @property {AiMarkCollapsePadding} [collapsePadding] - Collapses the component's layout footprint to match a plain icon on the chosen axes.
 * @extends Omit<IconProps, "svg">
 */
export type AiMarkProps = Omit<IconProps, "svg"> & {
  /**
   * The visual style of the AI mark.
   * @default default
   */
  type?: "default" | "gradient";
  /**
   * Configuration for popover or tooltip display.
   * Includes content, trigger label, and optional props for customization.
   * Popover config takes precedence over tooltip config.
   */
  popoverOrTooltipConfig?: AiMarkWithTooltipOrPopoverConfig;
  /**
   * When true, forces the pulsing morph animation to play regardless of
   * hover/focus state. Useful when AiMark is embedded inside an interactive
   * parent component (e.g. a Chip or Button) and the parent controls when
   * the animation should be active via its own hover or focus state.
   * @default false
   */
  forceAnimate?: boolean;
  /**
   * Collapses padding on the chosen axes so the component's layout footprint
   * matches a plain inline icon on those axes.
   *
   * For interactive variants (tooltip/popover), the ghost button's hit area is
   * unchanged — only the container's layout box shrinks.
   *
   * For non-interactive variants, padding equal to the ghost button's padding is
   * added to the non-collapsed axes, and omitted on the collapsed axes.
   *
   * - `"inline"` — collapse the inline axis (left/right)
   * - `"block"` — collapse the block axis (top/bottom)
   * - `"all"` — collapse both axes
   */
  collapsePadding?: AiMarkCollapsePadding;
};

/**
 * Component for displaying the ServiceTitan AI mark with optional tooltip or popover.
 *
 * Features:
 * - Two visual styles: default and gradient variants
 * - Optional tooltip on hover for contextual information
 * - Optional popover on click for more detailed content
 * - Forced animation mode for use within interactive parent components
 * - Uses ghost button appearance for subtle interaction
 * - Inherits all icon properties for size and styling customization
 * - Accessible with proper ARIA attributes from Button component
 * - Prevents event propagation when used with popover
 * - Configurable via popoverOrTooltipConfig for advanced customization
 * - Optional `collapsePadding` to reduce layout footprint to match a plain icon
 * - Animates on hover and focus when used with tooltip or popover
 * - Automatically respects the user's reduced motion preference
 *
 * @example
 * <AiMark
 *   type="default"
 *   popoverOrTooltipConfig={{
 *     type: "tooltip",
 *     content: "Powered by AI",
 *     triggerLabel: "AI feature information"
 *   }}
 * />
 *
 * @example
 * <AiMark
 *   type="gradient"
 *   popoverOrTooltipConfig={{
 *     type: "popover",
 *     content: <Text>AI feature details</Text>,
 *     triggerLabel: "Learn about AI features"
 *   }}
 * />
 *
 * @example
 * <AiMark type="default" forceAnimate={isHovered} />
 */
export const AiMark = (props: AiMarkProps) => {
  const {
    type = "default",
    popoverOrTooltipConfig,
    forceAnimate = false,
    collapsePadding,
    className,
    size = "medium",
    ...rest
  } = props;

  const inheritColor = type === "default";
  const collapseBlock =
    collapsePadding === "block" || collapsePadding === "all";
  const collapseInline =
    collapsePadding === "inline" || collapsePadding === "all";

  const baseContainerClassName = cx(
    styles["container"],
    styles[size],
    {
      [styles["collapse-block"]]: collapseBlock,
      [styles["collapse-inline"]]: collapseInline,
    },
    className,
  );

  if (popoverOrTooltipConfig) {
    return (
      <AiMarkInteractive
        config={popoverOrTooltipConfig}
        size={size}
        inheritColor={inheritColor}
        containerClassName={baseContainerClassName}
        forceAnimate={forceAnimate}
      />
    );
  }

  const nonInteractiveContainerClassName = cx(
    baseContainerClassName,
    styles["with-padding"],
  );

  if (forceAnimate) {
    return (
      <div
        className={nonInteractiveContainerClassName}
        style={{ color: rest.color }}
      >
        <AiMarkIconAnimated
          animate
          size={ICON_PIXEL_SIZES[size]}
          inheritColor={inheritColor}
        />
      </div>
    );
  }

  return (
    <div
      className={nonInteractiveContainerClassName}
      style={{ color: rest.color }}
      data-anv="ai-mark-icon"
    >
      <Icon
        {...rest}
        svg={type === "gradient" ? AiMarkGradientIcon : AiMarkDefaultIcon}
        size={size}
      />
    </div>
  );
};
